import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import moment from 'moment';
import store from '@/store';
import router from '@/router';
import axios from 'axios';
import { instBundle } from '@/axios.js';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);

import { beautyProfileInfo, beautyProfileMap } from '@/constants.js';

const axiosSupport = instBundle.axiosSupport;
const axiosShortUrl = instBundle.axiosShortUrl;

const getCsvFromA = async (blob, filename, apiUrl) => {
	const params = {
		admin_log: {
			path: router.currentRoute.fullPath,
			action: 'CSV',
			doc: { 'api-url': apiUrl },
		},
	};
	await axiosSupport.post('/admin/admin_log', params);

	const elem = document.createElement('a');
	const href = window.URL.createObjectURL(blob); // new Blob([res.data])
	elem.style = 'visibility:hidden';
	elem.href = href;
	elem.setAttribute('download', filename);
	document.body.appendChild(elem);
	elem.click();
	document.body.removeChild(elem);
};

const parseToCsvData = item => {
	return typeof item === 'function'
		? item()
		: `"${
				typeof item === 'number'
					? item
					: String(item || '')
							.replace(/"/g, '""')
							.replace(/,/g, '\,')
							.replace(/'/g, "'")
			}"`;
};

export const getRegexp = key => {
	return {
		url: /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
		httpsUrl:
			/(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,})/,
	}[key];
};

export const spinnerActive = () => {
	store.commit('toggleSpinner', true);
};

export const spinnerInactive = () => {
	store.commit('toggleSpinner', false);
};

export const getImagePath = (dir, fileName) => {
	if (getRegexp('url').test(dir)) {
		return `${dir}/${fileName}`;
	} else {
		const slash =
			typeof dir === 'string' && dir.substr(0, 1) !== '/' ? '/' : '';
		return `${store.state.env.IMAGE_STORAGE_PATH}${slash}${dir}/${fileName}`;
	}
};

export const getIdRegister = (defaultId = 636121) => {
	const env = process.env.NODE_ENV;
	let idRegister = store.state.login.idRegister;

	if (env === 'development') {
		idRegister = idRegister || defaultId;
	}

	return idRegister;
};

export const getS3Url = (ext, group) => {
	const url = () => {
		const host =
			process.env.NODE_ENV === 'development'
				? 'https://support.glowpick.net'
				: 'https://support.glowpick.com';

		return `${host}/system/aws/s3-presigned-post?file_ext=${ext}&group=${group}`;
	};
	const headers = {
		'x-api-key': 'b9d54cc0-5ea5-11ea-b7f9-41b4f2de8659',
	};

	return axios.get(url(), { headers });
};

export const uploadS3 = async (data, file) => {
	const uploadUrl = data.url;
	const formData = new FormData();

	Object.keys(data.fields).forEach(key => {
		formData.append(key, data.fields[key]);
	});

	formData.append('file', file);

	return axios.post(uploadUrl, formData);
};

/**
 * @description S3 에 이미지 파일 업로드 하기
 * @param { String } ext 이미지 파일 확장자
 * @param { String } group 업로드 할 스토리지 상의 하위 디렉토리
 * @param { File } file 이미지 파일
 * @returns {AxiosPromise<any>}
 */
export const imageUpload = async (ext, group, file) => {
	const { data } = await getS3Url(ext, group);
	return uploadS3(data, file);
};

export const imageRemove = async keys => {
	if (!keys || !keys.length) return;

	const url = () => {
		const host =
			process.env.NODE_ENV === 'development'
				? 'https://support.glowpick.net'
				: 'https://support.glowpick.com';

		return `${host}/system/aws/s3`;
	};

	const headers = {
		'x-api-key': 'b9d54cc0-5ea5-11ea-b7f9-41b4f2de8659',
	};

	if (!_.isArray(keys)) {
		keys = [keys];
	}

	keys = _.map(keys, key => ('/' == key[0] ? key.substr(1) : key));
	return axios.delete(url(), {
		headers,
		data: {
			keys,
		},
	});
};

export const setJosa = (string = '', josa = '') => {
	if (
		!string ||
		!josa ||
		string.trim().length.length === 0 ||
		josa.trim().length === 0
	) {
		throw 'Invalid Argument';
	} else if (typeof string !== 'string' || typeof josa !== 'string') {
		throw 'Invalid Argument Type';
	} else if (!/[가-힣]/.test(string)) {
		return string;
	}

	const josaSet = {
		을를: ['을', '를'],
		은는: ['은', '는'],
		이가: ['이', '가'],
		과와: ['과', '와'],
		로으로: ['으로', '로'],
	};

	if (!josaSet[josa]) {
		throw 'Argument josa allowed only: 을를, 은는, 이가, 과와, 로으로';
	}

	const lastChar = string.charCodeAt(string.length - 1);
	const index = (lastChar - 0xac00) % 28 > 0 ? 0 : 1;

	return `${string}${josaSet[josa][index]}`;
};

/**
 * @description 동일한 preSign 으로 S3 에 이미지 파일 업로드 하기
 * @param { String } url
 * @param { FormData } sign
 * @param { File } file
 * @returns {AxiosPromise<any>}
 */
export const imageUploadWithSign = async (url, sign, file) => {
	sign.delete('file');
	sign.append('file', file);

	return axios.post(url, sign);
};

export const checkEmail = email => {
	return /^([0-9a-zA-Z_\.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/.test(
		email
	);
};

export const addAuthCookie = auth => {
	document.cookie = `authenticate=${auth};expires=${moment().add(1, 'h').utc()};path=/;domain=${store.state.env.cookieDomain}`;
};

export default {
	/**
	 * @description 팝업창에 이미지 원본 노출
	 * @param {Array} imageList 원본 이미지 노출 할 이미지 경로 리스트
	 * @param {Number} maxWidth 팝업창 최대 width 지정
	 */
	async popupImages(imageList, maxWidth) {
		spinnerActive();

		const images = [];
		const getLoadedImg = src =>
			new Promise((resolve, reject) => {
				const img = new Image();

				img.src = src;
				img.onload = () => {
					img.style.width =
						img.naturalWidth >= maxWidth - 100 ? '100%' : 'auto';
					resolve(img);
				};
				img.onerror = reject;

				img.style.display = 'block';
				img.style.height = 'auto';
				img.alt = 'Popup Image';

				if (!isNaN(maxWidth)) {
					img.style.maxWidth = `${maxWidth}px`;
				}
			});

		const { width, height } = await imageList.reduce(
			async (promise, src) => {
				if (!src) return { width: 0, height: 0 };
				const result = await promise.then();
				const imgObj = await getLoadedImg(src);
				const [imgW, imgH] = [
					Number(imgObj.width),
					Number(imgObj.height),
				];

				images.push(imgObj);

				if (!isNaN(maxWidth) && imgW > maxWidth) {
					result.width = maxWidth;
					result.height += Math.floor(imgH * (maxWidth / imgW));
				} else {
					result.width = Math.max(result.width, imgW);
					result.height += imgH;
				}

				return Promise.resolve(result);
			},
			Promise.resolve({ width: 0, height: 0 })
		);

		const imgWin = window.open(
			'',
			'_blank',
			`width=${width}px, height=${height}px`
		);

		images.forEach(image => {
			imgWin.document.body.appendChild(image);
		});

		imgWin.document.documentElement.style.padding = '0';
		imgWin.document.documentElement.style.margin = '0';
		imgWin.document.body.style.padding = '0';
		imgWin.document.body.style.margin = '0';
		imgWin.document.title = imageList.join(',');

		spinnerInactive();
	},
	/**
	 * @description 어드민, 어드민2, 리액트어드민 으로 링크하기 위한 공통 메서드
	 * @param {Number, String} linkObj.type 링크의 종류
	 * @param {String} linkObj.path 링크경로
	 */
	linkToAdmin(linkObj) {
		const type = Number(linkObj.type);
		const curPath = router.currentRoute.path;
		const targetFullPath = linkObj.path;
		const targetPath = targetFullPath.split('?')[0];
		if (type === 0 && /^\/admin2/.test(targetPath)) {
			if (curPath === targetPath) {
				window.location.href = targetFullPath;
			} else {
				router.push(targetFullPath);
			}
		} else {
			const protocol = `${window.location.protocol}//`;
			const domain = store.state.typeModel[type];
			window.open(`${protocol}${domain}${targetFullPath}`, '_blank');
		}
	},
	getUrl(linkObj) {
		const type = Number(linkObj.type);
		const path = linkObj.path;
		if (type === 0 && path.split('/')[1] === 'admin2') {
			return path;
		} else {
			const protocol = `${window.location.protocol}//`;
			const domain = store.state.typeModel[type];
			return `${protocol}${domain}${path}`;
		}
	},
	emptyStrToNullNum(str) {
		return str === '' || str === null ? null : Number(str);
	},
	isEven(num) {
		return num % 2 === 0;
	},
	currency(value) {
		if (typeof value === 'number') value = String(value);
		return value
			? value.replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,')
			: value;
	},
	set00: str => str.toString().padStart(2, '0'),
	exportExcel(res) {
		const rawFilename = res.headers['content-disposition']
			.split(';')
			.filter(item => {
				if (item.indexOf('filename') > -1) return true;
			})[0];
		const filename = decodeURIComponent(
			rawFilename.trim().split('=')[1].replace('"', '').replace('"', '')
		);
		getCsvFromA(new Blob([res.data]), filename, res.config.url);
	},
	getCsv(rowsList, titleList = [], colsList = [], fileName, apiUrl) {
		try {
			const csvArr = rowsList.reduce((acc, rows, idx) => {
				const title = titleList[idx];
				const cols = colsList[idx];
				if (title) {
					acc.push(`${idx > 0 ? `\n\n${title}` : title}\r\n\n`);
				}
				if (cols) {
					acc.push(`${cols.join()}\r\n`);
				}
				const parsedRows = rows.map(row =>
					row.map(
						(item, itemIdx, arr) =>
							parseToCsvData(item) +
							(itemIdx + 1 === arr.length ? '\n' : ',')
					)
				);
				acc.push(...parsedRows);
				return acc;
			}, []);
			const blob = new Blob(['\ufeff' + flatten(csvArr).join('')], {
				type: 'text/csv;charset=utf-8;',
			});
			getCsvFromA(blob, `${fileName}.csv`, apiUrl);
		} catch (err) {
			console.error(err);
			alert(
				'CSV 파일을 다운로드하는데 실패했습니다. 개발팀에 문의하세요.'
			);
		}
	},
};

/**
 *
 * @param {String} type 매개변수 value 의 종류 - id: 등급ID, cnt: 리뷰갯수, name: 등급명
 * @param {String, Number} value
 * @param {String} returnType return 받을 type - id: 등급ID, name: 등급명
 */
export const getReviewerGrade = (type, value, returnType) => {
	const grade = [
		{
			id: 1,
			min: 1,
			max: 5,
			name: '비기너',
		},
		{
			id: 2,
			min: 6,
			max: 10,
			name: '익스플로러',
		},
		{
			id: 3,
			min: 11,
			max: 20,
			name: '콜렉터',
		},
		{
			id: 4,
			min: 21,
			max: 50,
			name: '엑스퍼트',
		},
		{
			id: 5,
			min: 51,
			name: '마스터',
		},
	];
	let findCB;
	if (type === 'id' || type === 'name') {
		findCB = item => item[type] === value;
	} else if (type === 'cnt') {
		findCB = item =>
			item.min <= value &&
			(value <= item.max || !item.hasOwnProperty('max'));
	} else {
		return null;
	}
	const gradeObj = grade.filter(findCB)[0];
	return gradeObj ? gradeObj[returnType] : null;
};

export const isLinkValueRequired = id => {
	return id === 'profile_edit_id'
		? false
		: id === 'outlink' || id.endsWith('_id');
};

export const useObservers = () => {
	const intersection = (element, cb = () => {}, options = {}) => {
		const ratio = options.ratio || 0;

		element.observer = new IntersectionObserver(changes => {
			changes.forEach(change => {
				if (options.continuous) {
					cb(change.intersectionRatio);
				} else if (change.intersectionRatio > ratio) {
					cb();
					element.observer.disconnect();
				}
			});
		});
		element.observer.observe(element);

		return () => {
			element.observer.disconnect();
			return null;
		};
	};

	const elementSize = (element, observeOnce = true, cb = () => {}) => {
		const getHeight = entries => {
			return entries[0].borderBoxSize
				? entries[0].borderBoxSize[0].blockSize
				: entries[0].target.offsetHeight;
		};

		if (observeOnce) {
			return new Promise(resolve => {
				const observer = new ResizeObserver(entries => {
					resolve({
						height: getHeight(entries),
					});
					observer.disconnect();
				});

				observer.observe(element);
			});
		} else {
			const observer = new ResizeObserver(entries => {
				cb(getHeight(entries));
			});

			observer.observe(element);
			return observer;
		}
	};

	const tabHeight = (
		tabElement,
		emitFunction,
		targetName,
		eventName = 'height-update'
	) => {
		return elementSize(tabElement, false, height => {
			emitFunction(eventName, {
				height,
				target: targetName,
			});
		});
	};

	const tabHeightOnce = (
		tabElement,
		emitFunction,
		targetName,
		eventName = 'height-update'
	) => {
		elementSize(tabElement).then(({ height }) => {
			emitFunction(eventName, {
				height,
				target: targetName,
			});
		});
	};

	return {
		intersection,
		elementSize,
		tabHeight,
		tabHeightOnce,
	};
};

export const getTime = () => {
	const offsetTime = new Date().getTimezoneOffset() * 60 * 1000;
	const convertTZ = (time, defValue = null, isPlus = true) =>
		time ? time + (isPlus ? offsetTime : offsetTime * -1) : defValue;

	const localToUtc = (time, defValue) => convertTZ(time, defValue);
	const utcToLocal = (time, defValue) => convertTZ(time, defValue, false);

	return {
		localToUtc,
		utcToLocal,
	};
};

// 하나의 엑셀 파일에 여러 시트를 생성하여 저장

export function exportJsonToExcel(sheets, fileName) {
	const workbook = XLSX.utils.book_new();

	sheets.forEach(sheet => {
		workbook.SheetNames.push(sheet.name);
	});

	sheets.forEach(sheet => {
		let worksheet;
		if (sheet.headers && sheet.data.length > 0) {
			const dataWithHeaders = [
				sheet.headers,
				...sheet.data.map(item => [...Object.values(item)]),
			];
			worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
			workbook.Sheets[sheet.name] = worksheet;
		}
	});

	XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function formattedDate(dateString) {
	if (!dateString || dateString.length < 8) return '';

	const year = dateString.slice(0, 4);
	const month = dateString.slice(4, 6);
	const day = dateString.slice(6, 8);
	const hour = dateString.slice(8, 10) || '00';
	const minute = dateString.slice(10, 12) || '00';
	const second = dateString.slice(12, 14) || '00';

	const date = `${year}-${month}-${day} ${hour}:${minute}:${second}`;

	return date;
}

export const durationText = (date, format = 'YYYY-MM-DDTHH:mm:ss.SSSZ') => {
	const now = dayjs();
	const to = typeof date === 'string' ? dayjs(date, format) : dayjs(date);

	const durations = dayjs.duration(now.diff(to));
	const diffInMonths = now.diff(to, 'month');
	const diffInDays = now.diff(to, 'day');

	const startOfThisMonth = now.startOf('month');
	const endOfPreviousMonth = to.endOf('month');

	const isExactMonth = startOfThisMonth.isSame(endOfPreviousMonth, 'day');
	const adjustedMonths = isExactMonth ? diffInMonths : diffInMonths + 1;

	const exactMatchConditions = [
		[d => d.asMinutes() < 1, () => '방금전'],
		[
			d => d.asMinutes() >= 1 && d.asHours() < 1,
			d => `${Math.floor(d.asMinutes())}분전`,
		],
		[
			d => d.asHours() >= 1 && d.asDays() < 1,
			d => `${Math.floor(d.asHours())}시간전`,
		],
		[
			d => d.asDays() >= 1 && d.asMonths() < 1,
			d => `${Math.floor(d.asDays())}일전`,
		],
		[
			d => adjustedMonths >= 1 && adjustedMonths < 12,
			d => `${adjustedMonths}개월전`,
		],
		[d => adjustedMonths === 12, () => `12개월전`],
		[d => d.asYears() >= 1, d => `${Math.floor(d.asYears())}년전`],
	];

	return match(exactMatchConditions, to.format('YYYY.MM.DD'))(durations);
};

export const match = (conditions, defaultValue) => value => {
	for (const [condition, result] of conditions) {
		if (condition(value)) return result(value);
	}
	return defaultValue;
};

export const beautyProfileTitleFromIdList = (idList = [], type = null) => {
	idList = compact(idList || []).map(id => String(id));

	return type
		? idList
				.filter(targetId =>
					beautyProfileInfo
						.filter(({ profileType }) => profileType === type)
						.map(({ id }) => id)
						.some(id => id === targetId)
				)
				.map(targetId => beautyProfileMap[targetId])
				.join(',') || '-'
		: idList
				.map(
					id =>
						beautyProfileInfo.find(item => item.id === id)
							.profileName
				)
				.join(' / ') || '-';
};
export const findNoUser = (list, targetList) =>
	list.reduce((acc, pdtObj, pdtIdx) => {
		const targetWinnerList = targetList[pdtIdx].candidates;
		pdtObj.candidates.forEach(winnerItem => {
			const same = targetWinnerList.some(
				historyWinnerItem =>
					historyWinnerItem.idRegister === winnerItem.idRegister
			);
			if (!same) acc++;
		});
		return acc;
	}, 0);

export const eventUserAnswerList = (eventQuestionAnswer, eventApplyData) => {
	return eventApplyData
		.map(({ idEventQuestion, applyType }) => {
			const regex = new RegExp(`(?:^|,)${idEventQuestion}([^,]*)`, 'g');
			const matches = Array.from(eventQuestionAnswer.matchAll(regex));
			const curAnswer = matches.map(match => match[1].trim());

			return {
				values:
					applyType === 3
						? curAnswer.map(
								value =>
									`${store.state.env.IMAGE_STORAGE_PATH}${value}`
							)
						: curAnswer,
				applyType,
			};
		})
		.sort((a, b) => a.applyType - b.applyType);
};

export const getShortUrl = async url => {
	const params = { longUrl: url };
	const { data } = await axiosShortUrl.post('/api/shorturl', params);
	return process.env.VUE_APP_API_SHORT_URL + '/' + data.shortUrl;
};
